import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { LoginDetailsCacheDto } from "../models/login-cache.model";
import { LoginCacheService } from "./login-cache.service";

/** Keys that must not be stored in browser sessionStorage — only in-memory + server cache API. */
export const LOGIN_DETAILS_STORAGE_KEYS = new Set([
  "token",
  "userId",
  "userDetails",
  "userMaster",
  "role",
  "branchId",
  "companyId",
  "createdUserId",
  "updatedUserId",
  "userEmail",
  "refreshToken",
  "approvStatesIds",
  "approvProductIds",
  "sessionId",
  "userLATInterval",
  "documentIds",
]);

/** Encrypted sessionStorage keys used only to restore logdetails after page refresh. */
export const LOGIN_AUTH_BOOTSTRAP_KEYS = {
  cacheSessionId: "cacheSessionId",
  authRefreshToken: "authRefreshToken",
  /** Encrypted sessionStorage copy used only to re-auth before logdetails GET on refresh. */
  authAccessToken: "authAccessToken",
} as const;

@Injectable({
  providedIn: "root",
})
export class LoginDetailsStorageService {
  private readonly loginCacheService = inject(LoginCacheService);
  private readonly values = new Map<string, string>();
  private static bridgeInstalled = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private loaded = false;

  /** Remove legacy login keys that were written to real sessionStorage before migration. */
  purgeNativeLoginKeysFromBrowser(): void {
    if (typeof window === "undefined") {
      return;
    }
    const nativeRemove = Storage.prototype.removeItem;
    LOGIN_DETAILS_STORAGE_KEYS.forEach((key) => {
      try {
        nativeRemove.call(window.sessionStorage, key);
      } catch {
        /* ignore */
      }
    });
  }

  /** Install after SecureSessionStorageService.init() so login keys bypass encrypted sessionStorage. */
  initSessionStorageBridge(): void {
    if (LoginDetailsStorageService.bridgeInstalled || typeof window === "undefined") {
      return;
    }

    const storageProto = Object.getPrototypeOf(window.sessionStorage) as Storage;
    const wrappedGetItem = storageProto.getItem;
    const wrappedSetItem = storageProto.setItem;
    const wrappedRemoveItem = storageProto.removeItem;
    const store = this;

    storageProto.getItem = function (key: string): string | null {
      if (this === window.sessionStorage && LOGIN_DETAILS_STORAGE_KEYS.has(key)) {
        return store.getItem(key);
      }
      return wrappedGetItem.call(this, key);
    };

    storageProto.setItem = function (key: string, value: string): void {
      if (this === window.sessionStorage && LOGIN_DETAILS_STORAGE_KEYS.has(key)) {
        store.setItem(key, value);
        return;
      }
      wrappedSetItem.call(this, key, value);
    };

    storageProto.removeItem = function (key: string): void {
      if (this === window.sessionStorage && LOGIN_DETAILS_STORAGE_KEYS.has(key)) {
        store.removeItem(key);
        return;
      }
      wrappedRemoveItem.call(this, key);
    };

    LoginDetailsStorageService.bridgeInstalled = true;
  }

  getItem(key: string): string | null {
    if (!LOGIN_DETAILS_STORAGE_KEYS.has(key)) {
      return null;
    }
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (!LOGIN_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.set(key, String(value));
    this.syncBootstrapKeys(key, String(value));
    this.schedulePersist();
  }

  removeItem(key: string): void {
    if (!LOGIN_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.delete(key);
    this.schedulePersist();
  }

  clear(): void {
    this.values.clear();
    this.loaded = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getCacheSessionId(): string | null {
    return this.getItem("sessionId") ?? this.getItem("userId") ?? null;
  }

  buildCachePayload(
    overrides: Partial<LoginDetailsCacheDto> = {}
  ): LoginDetailsCacheDto {
    const payload: LoginDetailsCacheDto = {
      token: this.getItem("token") ?? "",
      userId: this.getItem("userId") ?? "",
      userDetails: this.getItem("userDetails") ?? "",
      userMaster: this.getItem("userMaster") ?? "",
      role: this.getItem("role") ?? "",
      branchId: this.toCacheInt(this.getItem("branchId")),
      companyId: this.toCacheInt(this.getItem("companyId")),
      createdUserId: this.toCacheInt(this.getItem("createdUserId")),
      updatedUserId: this.toCacheInt(this.getItem("updatedUserId")),
      userEmail: this.getItem("userEmail") ?? "",
      refreshToken: this.getItem("refreshToken") ?? "",
      approvedStates: this.serializeApprovedList(this.getItem("approvStatesIds")),
      approvedProducts: this.serializeApprovedList(
        this.getItem("approvProductIds")
      ),
      sessionId: this.getItem("sessionId") ?? this.getBootstrapSessionId() ?? "",
      userLATInterval: this.toCacheInt(this.getItem("userLATInterval")),
      documentIds: this.serializeApprovedList(this.getItem("documentIds")),
      ...overrides,
    };

    return this.normalizeNumericFields(payload);
  }

  applyFromCacheDto(data: LoginDetailsCacheDto): void {
    this.setValueQuiet("token", data.token);
    this.setValueQuiet("userId", data.userId);
    if (data.sessionId !== undefined && data.sessionId !== null && data.sessionId !== "") {
      const sid = String(data.sessionId);
      this.values.set("sessionId", sid);
      this.persistBootstrapSessionId(sid);
    }

    this.setValueQuiet("userDetails", data.userDetails);
    this.setValueQuiet("userMaster", data.userMaster);
    this.setValueQuiet("role", data.role);
    this.setValueQuiet("branchId", data.branchId);
    this.setValueQuiet("companyId", data.companyId);
    this.setValueQuiet("createdUserId", data.createdUserId);
    this.setValueQuiet("updatedUserId", data.updatedUserId);
    this.setValueQuiet("userEmail", data.userEmail);
    this.setValueQuiet("refreshToken", data.refreshToken);
    if (data.refreshToken) {
      this.persistBootstrapRefreshToken(String(data.refreshToken));
    }
    this.setValueQuiet("userLATInterval", data.userLATInterval);

    if (data.approvedStates) {
      this.values.set(
        "approvStatesIds",
        this.deserializeApprovedList(data.approvedStates)
      );
    }
    if (data.approvedProducts) {
      this.values.set(
        "approvProductIds",
        this.deserializeApprovedList(data.approvedProducts)
      );
    }
    if (data.documentIds) {
      this.values.set(
        "documentIds",
        this.deserializeApprovedList(data.documentIds)
      );
    }

    this.loaded = true;
  }

  getBootstrapSessionId(): string | null {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage.getItem(LOGIN_AUTH_BOOTSTRAP_KEYS.cacheSessionId);
  }

  getBootstrapRefreshToken(): string | null {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage.getItem(LOGIN_AUTH_BOOTSTRAP_KEYS.authRefreshToken);
  }

  hasBootstrapAccessToken(): boolean {
    return !!this.getBootstrapAccessToken();
  }

  getBootstrapAccessToken(): string | null {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage.getItem(LOGIN_AUTH_BOOTSTRAP_KEYS.authAccessToken);
  }

  /** Rehydrate in-memory JWT from encrypted sessionStorage after a full page refresh. */
  restoreAccessTokenFromBootstrapStorage(): boolean {
    const token = this.getBootstrapAccessToken();
    if (!token) {
      return false;
    }
    this.values.set("token", token);
    return true;
  }

  loadFromCache(sessionId?: string | number): Observable<boolean> {
    const token = this.getItem("token");
    const sid =
      sessionId ?? this.getCacheSessionId() ?? this.getBootstrapSessionId();

    if (!token || !sid) {
      return of(false);
    }

    return this.loginCacheService.get(sid).pipe(
      tap((data) => {
        if (data) {
          this.applyFromCacheDto(data);
        }
      }),
      map((data) => !!data),
      catchError(() => of(false))
    );
  }

  saveToCache(
    overrides: Partial<LoginDetailsCacheDto> = {},
    sessionIdOverride?: string | number
  ): Observable<LoginDetailsCacheDto | null> {
    const sessionId =
      sessionIdOverride ?? overrides.sessionId ?? this.getCacheSessionId();

    if (!sessionId) {
      return of(null);
    }

    const payload = this.buildCachePayload({
      ...overrides,
      sessionId: String(sessionId),
    });

    this.applyFromCacheDto(payload);

    return this.loginCacheService.set(sessionId, payload).pipe(
      map(() => payload),
      catchError(() => of(payload))
    );
  }

  removeFromCache(sessionId?: string | number): Observable<unknown> {
    const sid = sessionId ?? this.getCacheSessionId();
    if (!sid) {
      return of(null);
    }
    return this.loginCacheService.remove(sid);
  }

  private setValueQuiet(key: string, value: unknown): void {
    if (value !== undefined && value !== null && value !== "") {
      const normalized = String(value);
      this.values.set(key, normalized);
      this.syncBootstrapKeys(key, normalized);
    }
  }

  private syncBootstrapKeys(key: string, value: string): void {
    if (key === "token") {
      this.persistBootstrapAccessToken(value);
    }
    if (key === "sessionId") {
      this.persistBootstrapSessionId(value);
    }
    if (key === "refreshToken") {
      this.persistBootstrapRefreshToken(value);
    }
    if (key === "userId" && !this.getItem("sessionId")) {
      this.persistBootstrapSessionId(value);
    }
  }

  private persistBootstrapAccessToken(token: string): void {
    if (typeof sessionStorage === "undefined" || !token) {
      return;
    }
    sessionStorage.setItem(
      LOGIN_AUTH_BOOTSTRAP_KEYS.authAccessToken,
      token
    );
  }

  private persistBootstrapSessionId(sessionId: string): void {
    if (typeof sessionStorage === "undefined" || !sessionId) {
      return;
    }
    sessionStorage.setItem(
      LOGIN_AUTH_BOOTSTRAP_KEYS.cacheSessionId,
      sessionId
    );
  }

  private persistBootstrapRefreshToken(refreshToken: string): void {
    if (typeof sessionStorage === "undefined" || !refreshToken) {
      return;
    }
    sessionStorage.setItem(
      LOGIN_AUTH_BOOTSTRAP_KEYS.authRefreshToken,
      refreshToken
    );
  }

  private schedulePersist(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      const sessionId = this.getCacheSessionId();
      if (sessionId) {
        this.saveToCache().subscribe();
      }
    }, 300);
  }

  private serializeApprovedList(value: string | null): string {
    if (!value) {
      return "";
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item)).join(",");
      }
    } catch {
      return value;
    }
    return value;
  }

  /** API expects branchId, companyId, createdUserId, updatedUserId, userLATInterval as integers. */
  toCacheInt(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === "") {
      return 0;
    }
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  }

  private normalizeNumericFields(
    payload: LoginDetailsCacheDto
  ): LoginDetailsCacheDto {
    return {
      ...payload,
      branchId: this.toCacheInt(payload.branchId),
      companyId: this.toCacheInt(payload.companyId),
      createdUserId: this.toCacheInt(payload.createdUserId),
      updatedUserId: this.toCacheInt(payload.updatedUserId),
      userLATInterval: this.toCacheInt(payload.userLATInterval),
    };
  }

  private deserializeApprovedList(value: string | undefined | null): string {
    if (!value) {
      return JSON.stringify([]);
    }
    if (value.trim().startsWith("[")) {
      return value;
    }
    const ids = value
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    const asNumbers = ids.map((id) => Number(id));
    if (asNumbers.every((n) => !Number.isNaN(n))) {
      return JSON.stringify(asNumbers);
    }
    return JSON.stringify(ids);
  }
}
